import find from 'lodash-es/find';
import memoize from 'lodash-es/memoize';
import { Categories, Category } from 'module/Model/Categories';
import { createSelector } from 'reselect';
import { nn, nu, se } from 'module/Common';
import { Post } from 'module/Model/Post';
import { PostSelector } from 'module/Redux/Post';
import { Route, RouteType } from 'module/Model/Routes';
import { RouterSelector } from 'module/Redux/Router';
import { State } from 'module/Redux';
import { Wish } from 'module/Model/Wish';

export namespace CategoriesSelector {
  export const selectAll = (s: State) => se(s, 'categories', s.categories);

  export const selectForRoute = (s: State, route: Route): Wish<Category> => {
    const categoriesWish = selectAll(s);
    if (route === null) {
      return Wish.createVoid();
    }
    if (route.type === RouteType.Category) {
      return Wish.transform(categoriesWish, categories => {
        const maybeCategory = find(categories, cat => Category.getId(cat) === route.id);
        if (maybeCategory === undefined) {
          return Wish.createRejected(`Can't find category with id ${route.id}`);
        }
        return maybeCategory;
      });
    }
    return Wish.createVoid();
  };

  export const selectForMatchingRoute = createSelector(
    selectAll,
    RouterSelector.selectMatchingRoute,
    (categoriesWish, route): Wish<Category> | null => {
      if (route === null) {
        return null;
        // return Wish.createVoid();
      }
      if (route.type === RouteType.Category) {
        return Wish.transform(categoriesWish, categories => {
          const maybeCategory = find(categories, cat => Category.getId(cat) === route.id);
          if (maybeCategory === undefined) {
            return Wish.createRejected(`Can't find category with id ${route.id}`);
          }
          return maybeCategory;
        });
      }
      return null;
    }
  );

  export const selectForCurrentItem = createSelector(
    selectAll,
    PostSelector.selectCurrent,
    (categoriesWish, currentPostWish) => {
      return Wish.combine([categoriesWish, currentPostWish], (categories, currentPost): Array<Category> => {
        return Post.getCategoryIds(currentPost)
          .map(categoryId => {
            const category = find(categories, cat => Category.getId(cat) === categoryId);
            if (category === undefined) {
              console.warn(`Can't find category with id ${categoryId}`);
            }
            return category;
          })
          .filter<Category>((category): category is Category => {
            return category !== undefined;
          });
      });
    }
  );

  const allExceptUncached = (catsWish: Wish<Categories>, catsId: number): Wish<Categories> => {
    return Wish.transform(catsWish, cats => {
      return cats.filter(cat => Category.getId(cat) !== catsId);
    });
  };

  const allExcept = memoize(allExceptUncached);

  export const selectNextLinks = (s: State): Wish<Categories> => {
    const lastRoute = RouterSelector.selectLastGridRoute(s);
    const catsWish = selectForCurrentItem(s);
    if (lastRoute === null) {
      return catsWish;
    }
    if (lastRoute.type !== RouteType.Category) {
      return catsWish;
    }
    const currentCatId = nn(lastRoute.id);
    return allExcept(catsWish, currentCatId);
  };

  export const nextCategory = (s: State): Wish<Category> | null => {
    const lastRoute = RouterSelector.selectLastGridRoute(s);
    if (lastRoute === null) {
      return null;
    }
    if (lastRoute.type !== RouteType.Category) {
      return null;
    }
    const catsWish = selectAll(s);
    const currentCatId = nn(lastRoute.id);
    return Wish.transform(catsWish, cats => {
      return nu(find(cats, cat => Category.getId(cat) === currentCatId));
    });
  };
}



// WEBPACK FOOTER //
// ./src/module/Redux/Categories/CategoriesSelector.ts